import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { fmtDuration } from "@/lib/utils";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { Timer } from "lucide-react";
import { toast } from "sonner";

const FREE_TEXT = "__livre__";

/**
 * Janela ao centro para arrancar uma interação num cliente: escolhe uma tarefa
 * existente ou descreve o que vai fazer. Se já houver uma interação do próprio
 * utilizador noutro cliente, o backend fecha-a automaticamente.
 */
export function StartInteractionSheet({ client, open, onOpenChange }) {
  const qc = useQueryClient();
  const [taskId, setTaskId] = useState(FREE_TEXT);
  const [description, setDescription] = useState("");
  const [linkTask, setLinkTask] = useState(true);
  const [saving, setSaving] = useState(false);

  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks", "client", client?.id],
    queryFn: async () => (await api.get("/tasks", { params: { clientId: client.id } })).data,
    enabled: Boolean(open && client?.id),
  });
  // Muitas tarefas foram criadas sem cliente (com o nome no título): mostra
  // também as da própria pessoa que não têm cliente, para as poder escolher.
  const { data: myTasks = [] } = useQuery({
    queryKey: ["tasks", "mine"],
    queryFn: async () => (await api.get("/tasks", { params: { mine: true } })).data,
    enabled: Boolean(open),
  });
  const openTasks = tasks.filter((t) => t.status !== "CONCLUIDO");
  const clientTaskIds = new Set(openTasks.map((t) => t.id));
  const myLooseTasks = myTasks.filter((t) => t.status !== "CONCLUIDO" && !t.clientId && !clientTaskIds.has(t.id));
  const selectedTask = myLooseTasks.find((t) => t.id === taskId);

  useEffect(() => {
    if (open) { setTaskId(FREE_TEXT); setDescription(""); setLinkTask(true); }
  }, [open, client?.id]);

  const start = async () => {
    if (taskId === FREE_TEXT && !description.trim()) {
      toast.error("Escolhe uma tarefa ou descreve o que vais fazer");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/interactions/start", {
        clientId: client.id,
        taskId: taskId === FREE_TEXT ? "" : taskId,
        description: description.trim(),
      });
      qc.invalidateQueries({ queryKey: ["interaction-active"] });
      qc.invalidateQueries({ queryKey: ["interactions"] });
      if (selectedTask && linkTask) {
        // A interação já arrancou; se a associação falhar, só avisa.
        try {
          await api.put(`/tasks/${selectedTask.id}`, { ...selectedTask, clientId: client.id });
          qc.invalidateQueries({ queryKey: ["tasks"] });
        } catch (e) {
          toast.error(`Não foi possível associar a tarefa a ${client.name}: ${apiError(e.response?.data?.detail)}`);
        }
      }
      if (data.stopped) {
        toast.success(`Interação em ${client.name} iniciada — ${data.stopped.clientName} parou (${fmtDuration(data.stopped.minutes)})`);
      } else {
        toast.success(`Interação em ${client.name} iniciada`);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100%-2rem)] max-w-lg rounded-2xl"
        data-testid="interaction-sheet"
      >
        <DialogHeader className="items-center text-center sm:text-center">
          <DialogTitle className="flex items-center justify-center gap-2 font-poppins">
            <Timer className="h-4 w-4 text-primary" /> Gerar interação · {client?.name}
          </DialogTitle>
          <DialogDescription>Para automaticamente ao iniciares outra interação.</DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          <div>
            <Label className="text-sm">Tarefa (opcional)</Label>
            <Select value={taskId} onValueChange={setTaskId}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={FREE_TEXT}>Sem tarefa — descrever abaixo</SelectItem>
                {openTasks.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Deste cliente</SelectLabel>
                    {openTasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                  </SelectGroup>
                )}
                {myLooseTasks.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>As minhas tarefas sem cliente</SelectLabel>
                    {myLooseTasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            {selectedTask && (
              <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="interaction-link-task">
                <Checkbox checked={linkTask} onCheckedChange={(v) => setLinkTask(Boolean(v))} />
                Associar esta tarefa a {client?.name}
              </label>
            )}
          </div>
          <div>
            <Label className="text-sm">{taskId === FREE_TEXT ? "O que vais fazer" : "Nota (opcional)"}</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex.: criativos para os stories desta semana"
              className="mt-1"
              autoFocus
              data-testid="interaction-description"
            />
          </div>
        </div>

        <DialogFooter className="mt-5 gap-2 sm:justify-center sm:space-x-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={start} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="interaction-start">
            <Timer className="h-4 w-4 mr-1.5" /> Iniciar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
